import { atom } from 'jotai';
import { DEFAULT_SELECTED_METRICS } from '../constants';
import { DATE_SHORTCUTS } from '@src/utils/constants';
import { metricKeys } from '@pulse/metrics';
import { getDefaultFlowConfig } from '../FlowPanel/utils/flowConfigUtils';

export type FilterType = {
  name?: string;
  rule: any;
  _rKey?: number;
  fid?: number | undefined;
};

// Default filter value used in multiple places
export const defaultFilter: FilterType = {
  rule: [],
  _rKey: 0,
  fid: undefined,
};

// Interval atom - NOT persisted, resets to default on page refresh
export const appliedIntervalAtom = atom<{
  from: string | null;
  to: string | null;
  shortcut: string;
  dashboardType: string;
  granularity?: string;
}>({
  from: null,
  to: null,
  shortcut: DATE_SHORTCUTS.past24Hours,
  dashboardType: 'overview',
  granularity: 'PT1H',
});

// DOD (Day-over-Day) toggle state shared across components
export const enableDodAtom = atom<boolean>(false);

// Unit toggle state for metrics (# vs %)
export const metricUnitAtom = atom<'#' | '%'>('#');

// Single source of truth for overview state (server-side persistence only)
export const persistedOverviewAtom = atom<{
  flows: Record<string, { id: string; order: number; [key: string]: any }>;
  frustrationSignals: Record<string, { id: string; order: number; [key: string]: any }>;
  events: Record<string, { id: string; order: number; [key: string]: any }>;
  appliedFilter: FilterType;
  viewMode: 'table' | 'widget';
  eventsViewMode?: 'table' | 'widget';
  selectedMetrics?: string[];
  percentileMetrics?: string[];
  percentiles?: string[];
  networkMetrics?: Record<string, { id: string; order: number }>;
  currentKpiId?: number | null;
  panels: {
    audience: boolean;
    flows: boolean;
    alerts: boolean;
    frustration?: boolean;
    events?: boolean;
    network?: boolean;
    percentile?: boolean;
  };
}>({
  flows: {},
  frustrationSignals: {},
  events: {},
  appliedFilter: defaultFilter,
  viewMode: 'table', // default to table view for flows
  eventsViewMode: 'table', // default to table view for events
  networkMetrics: {
    [metricKeys.mins_with_0_nwr_failure]: { id: metricKeys.mins_with_0_nwr_failure, order: 1 },
    [metricKeys.mins_with_4xx_nwr_failure]: { id: metricKeys.mins_with_4xx_nwr_failure, order: 2 },
    [metricKeys.mins_with_5xx_nwr_failure]: { id: metricKeys.mins_with_5xx_nwr_failure, order: 3 },
    [metricKeys.mins_with_high_nwr_duration]: {
      id: metricKeys.mins_with_high_nwr_duration,
      order: 4,
    },
  },
  percentileMetrics: [
    metricKeys.avg_cvv_soft_navigation_time,
    metricKeys.AverageMinutesPerPage,
    metricKeys.AppStartupTime,
  ],
  percentiles: ['avg', 'p99'],
  selectedMetrics: DEFAULT_SELECTED_METRICS,
  panels: {
    audience: true, // Enable by default for new users
    flows: true, // Enable by default for new users
    alerts: false, // Disable by default for new users
    frustration: true, // Enable by default for new users
    events: true, // Enable by default for new users
    network: false, // Disable by default for new users
    percentile: false, // Disable by default for new users
  },
});

// Filter atom that automatically syncs with persistence
export const appliedFilterAtom = atom(
  (get) => {
    const persisted = get(persistedOverviewAtom);
    return persisted.appliedFilter || defaultFilter;
  },
  (get, set, newFilter: FilterType) => {
    const currentState = get(persistedOverviewAtom);
    set(persistedOverviewAtom, {
      ...currentState,
      appliedFilter: newFilter,
    });
  },
);

// Selected flows for operations
export const selectedFlowsAtom = atom<string[]>([]);

// Selected frustration signals for operations
export const selectedFrustrationSignalsAtom = atom<string[]>([]);

// Selected metrics (persisted as part of the overview state)
export const selectedMetricsAtom = atom(
  (get) => {
    const persisted = get(persistedOverviewAtom);
    return persisted.selectedMetrics || DEFAULT_SELECTED_METRICS;
  },
  (get, set, newMetrics: string[]) => {
    const current = get(persistedOverviewAtom);
    set(persistedOverviewAtom, {
      ...current,
      selectedMetrics: newMetrics,
    });
  },
);

// Derived: current KPI id for overview, default 5 when persistence has none (DPI/app mode)
export const currentKpiIdAtom = atom(
  (get) => {
    const persisted = get(persistedOverviewAtom);
    return persisted.currentKpiId ?? 5;
  },
  (get, set, kpiId: number) => {
    const current = get(persistedOverviewAtom);
    set(persistedOverviewAtom, {
      ...current,
      currentKpiId: kpiId,
    });
  },
);

// Initialize overview with default config
export const initOverviewDataAtom = atom(null, (get, set) => {
  const defaultConfig = getDefaultFlowConfig();
  const { flows: flowIds } = defaultConfig;

  const formattedFlows = {};
  flowIds.forEach((flowId, index) => {
    formattedFlows[flowId] = {
      id: flowId,
      order: index + 1,
    };
  });

  const currentState = get(persistedOverviewAtom);
  set(persistedOverviewAtom, {
    ...currentState,
    flows: formattedFlows,
    frustrationSignals: currentState.frustrationSignals || {}, // Preserve existing or empty
    appliedFilter: currentState.appliedFilter || defaultFilter,
    viewMode: currentState.viewMode || 'table',
  });
});

// Add flow to store
export const addFlowAtom = atom(null, (get, set, flowId: string) => {
  const { flows } = get(persistedOverviewAtom);
  const nextOrder = Object.keys(flows).length + 1;
  const currentState = get(persistedOverviewAtom);

  set(persistedOverviewAtom, {
    ...currentState,
    flows: {
      ...flows,
      [flowId]: { id: flowId, order: nextOrder },
    },
  });
});

// Remove flow and reorder remaining flows
export const removeFlowAtom = atom(null, (get, set, flowId: string) => {
  const { flows } = get(persistedOverviewAtom);
  const newFlows = { ...flows };

  delete newFlows[flowId];

  const orderedIds = Object.keys(newFlows).sort((a, b) => newFlows[a].order - newFlows[b].order);
  orderedIds.forEach((id, index) => {
    newFlows[id].order = index + 1;
  });

  const currentState = get(persistedOverviewAtom);
  set(persistedOverviewAtom, {
    ...currentState,
    flows: newFlows,
  });
});

// Update flow order for drag and drop reordering
export const updateFlowOrderAtom = atom(null, (get, set, newOrderIds: string[]) => {
  const { flows } = get(persistedOverviewAtom);

  const updatedFlows = {};
  newOrderIds.forEach((id, index) => {
    updatedFlows[id] = {
      ...(flows[id] || {}),
      id,
      order: index + 1,
    };
  });

  const currentState = get(persistedOverviewAtom);
  set(persistedOverviewAtom, {
    ...currentState,
    flows: updatedFlows,
  });
});

// Update view mode (table or widget) for flows
export const updateViewModeAtom = atom(null, (get, set, newViewMode: 'table' | 'widget') => {
  const currentState = get(persistedOverviewAtom);

  if (currentState.viewMode === newViewMode) {
    return;
  }

  set(persistedOverviewAtom, {
    ...currentState,
    viewMode: newViewMode,
  });
});

// Update view mode (table or widget) for events
export const updateEventsViewModeAtom = atom(null, (get, set, newViewMode: 'table' | 'widget') => {
  const currentState = get(persistedOverviewAtom);

  if (currentState.eventsViewMode === newViewMode) {
    return;
  }

  set(persistedOverviewAtom, {
    ...currentState,
    eventsViewMode: newViewMode,
  });
});

export const panelsAtom = atom(
  (get) => {
    const persisted = get(persistedOverviewAtom);
    // For existing users without panel persistence, enable metric and flow panels, disable alert panel
    return (
      persisted.panels || {
        audience: true,
        flows: true,
        alerts: false,
        frustration: true,
        events: true,
        network: false,
        percentile: false,
      }
    );
  },
  (get, set, newPanels: any) => {
    const currentState = get(persistedOverviewAtom);
    set(persistedOverviewAtom, {
      ...currentState,
      panels: {
        ...(currentState.panels || {
          audience: true,
          flows: true,
          alerts: false,
          frustration: true,
          events: true,
          network: false,
          percentile: false,
        }),
        ...newPanels,
      },
    });
  },
);

// Add frustration signal to store
export const addFrustrationSignalAtom = atom(null, (get, set, signalId: string) => {
  const { frustrationSignals } = get(persistedOverviewAtom);
  const nextOrder = Object.keys(frustrationSignals).length + 1;
  const currentState = get(persistedOverviewAtom);

  set(persistedOverviewAtom, {
    ...currentState,
    frustrationSignals: {
      ...frustrationSignals,
      [signalId]: { id: signalId, order: nextOrder },
    },
  });
});

// Remove frustration signal and reorder remaining signals
export const removeFrustrationSignalAtom = atom(null, (get, set, signalId: string) => {
  const { frustrationSignals } = get(persistedOverviewAtom);
  const newSignals = { ...frustrationSignals };

  delete newSignals[signalId];

  const orderedIds = Object.keys(newSignals).sort(
    (a, b) => newSignals[a].order - newSignals[b].order,
  );
  orderedIds.forEach((id, index) => {
    newSignals[id].order = index + 1;
  });

  const currentState = get(persistedOverviewAtom);
  set(persistedOverviewAtom, {
    ...currentState,
    frustrationSignals: newSignals,
  });
});

// Update frustration signal order for drag and drop reordering
export const updateFrustrationSignalOrderAtom = atom(null, (get, set, newOrderIds: string[]) => {
  const { frustrationSignals } = get(persistedOverviewAtom);

  const updatedSignals = {};
  newOrderIds.forEach((id, index) => {
    updatedSignals[id] = {
      ...(frustrationSignals[id] || {}),
      id,
      order: index + 1,
    };
  });

  const currentState = get(persistedOverviewAtom);
  set(persistedOverviewAtom, {
    ...currentState,
    frustrationSignals: updatedSignals,
  });
});

// Percentile metrics (persisted as part of the overview state)
export const percentileMetricsAtom = atom(
  (get) => {
    const persisted = get(persistedOverviewAtom);
    return (
      persisted.percentileMetrics || [
        metricKeys.avg_cvv_soft_navigation_time,
        metricKeys.AverageMinutesPerPage,
        metricKeys.AppStartupTime,
      ]
    );
  },
  (get, set, newMetrics: string[]) => {
    const current = get(persistedOverviewAtom);
    set(persistedOverviewAtom, {
      ...current,
      percentileMetrics: newMetrics,
    });
  },
);

// Selected percentiles (persisted as part of the overview state)
export const percentilesAtom = atom(
  (get) => {
    const persisted = get(persistedOverviewAtom);
    return persisted.percentiles || ['avg', 'p99'];
  },
  (get, set, newPercentiles: string[]) => {
    const current = get(persistedOverviewAtom);
    set(persistedOverviewAtom, {
      ...current,
      percentiles: newPercentiles,
    });
  },
);

// Add event to store
export const addEventAtom = atom(null, (get, set, eventId: string) => {
  const currentState = get(persistedOverviewAtom);
  const events = currentState.events || {};
  const nextOrder = Object.keys(events).length + 1;
  set(persistedOverviewAtom, {
    ...currentState,
    events: {
      ...events,
      [eventId]: { id: eventId, order: nextOrder },
    },
  });
});

// Remove event and reorder remaining events
export const removeEventAtom = atom(null, (get, set, eventId: string) => {
  const currentState = get(persistedOverviewAtom);
  const events = currentState.events || {};
  const newEvents = { ...events };
  delete newEvents[eventId];
  const orderedIds = Object.keys(newEvents).sort((a, b) => newEvents[a].order - newEvents[b].order);
  orderedIds.forEach((id, index) => {
    newEvents[id].order = index + 1;
  });
  set(persistedOverviewAtom, {
    ...currentState,
    events: newEvents,
  });
});

// Update engagement event order for drag and drop reordering
export const updateEventsOrderAtom = atom(null, (get, set, newOrderIds: string[]) => {
  const currentState = get(persistedOverviewAtom);
  const events = currentState.events || {};
  const updatedEvents = {};
  newOrderIds.forEach((id, index) => {
    updatedEvents[id] = {
      ...(events[id] || {}),
      id,
      order: index + 1,
    };
  });
  set(persistedOverviewAtom, {
    ...currentState,
    events: updatedEvents,
  });
});

export const selectedEventsAtom = atom<string[]>([]);

// Update network metrics order for drag and drop reordering
export const updateNetworkOrderAtom = atom(null, (get, set, newOrderIds: string[]) => {
  const currentState = get(persistedOverviewAtom);
  const networkMetrics = currentState.networkMetrics || {};

  const updatedNetworkMetrics: Record<string, { id: string; order: number }> = {};
  newOrderIds.forEach((id, index) => {
    updatedNetworkMetrics[id] = {
      ...(networkMetrics[id] || {}),
      id,
      order: index + 1,
    };
  });

  set(persistedOverviewAtom, {
    ...currentState,
    networkMetrics: updatedNetworkMetrics,
  });
});
